import React, { useState, useEffect } from 'react';

export function SettingsPanel({
  settings,
  storageStats,
  onFetchSettings,
  onUpdateSettings,
  onFetchStorageStats,
  onRunCleanup,
  onDeleteByDate,
}) {
  const [retentionDays, setRetentionDays] = useState(30);
  const [autoRecordAll, setAutoRecordAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [deletingDate, setDeletingDate] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    onFetchSettings();
    onFetchStorageStats();
  }, [onFetchSettings, onFetchStorageStats]);

  useEffect(() => {
    setRetentionDays(parseInt(settings?.retention_days ?? '30'));
    setAutoRecordAll(
      settings?.auto_record_all === '1' ||
      settings?.auto_record_all === 1 ||
      settings?.auto_record_all === true ||
      settings?.auto_record_all === 'true'
    );
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const result = await onUpdateSettings({
        retention_days: retentionDays,
        auto_record_all: autoRecordAll,
      });
      if (result.success) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to save settings' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleToggleAuto = async (checked) => {
    setAutoRecordAll(checked);
    setSaving(true);
    setMessage(null);
    try {
      const result = await onUpdateSettings({
        retention_days: retentionDays,
        auto_record_all: checked,
      });
      if (result.success) {
        setMessage({ type: 'success', text: `Auto Record ${checked ? 'enabled' : 'disabled'}` });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to update auto record' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Are you sure you want to delete old recordings now?')) {
      return;
    }

    setCleaning(true);
    setMessage(null);

    try {
      const result = await onRunCleanup();
      if (result.success) {
        const freedMB = ((result.freedBytes || 0) / 1024 / 1024).toFixed(2);
        setMessage({
          type: 'success',
          text: `Cleanup completed! Deleted ${result.deletedCount || 0} files, freed ${freedMB} MB`,
        });
        onFetchStorageStats();
      } else {
        setMessage({ type: 'error', text: result.error || 'Cleanup failed' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setCleaning(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleDeleteByDate = async (date) => {
    if (!window.confirm(`Are you sure you want to delete all recordings from ${date}?`)) {
      return;
    }

    setDeletingDate(date);
    setMessage(null);

    try {
      const result = await onDeleteByDate(date);
      if (result.success) {
        setMessage({
          type: 'success',
          text: `Deleted ${result.deletedCount} files from ${date}, freed ${result.freedMB} MB`,
        });
        onFetchStorageStats();
      } else {
        setMessage({ type: 'error', text: result.error || 'Delete failed' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setDeletingDate(null);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes / 1024 / 1024;
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="settings-panel">
      {/* Retention Settings */}
      <div className="settings-card">
        <h3>Recording Retention</h3>
        <p className="settings-desc">
          Recordings older than the specified number of days will be automatically deleted.
        </p>

        <div className="setting-row">
          <label htmlFor="retention">Keep recordings for (days)</label>
          <div className="input-group">
            <input
              id="retention"
              type="number"
              min="1"
              max="365"
              value={retentionDays}
              onChange={(e) => setRetentionDays(parseInt(e.target.value) || 1)}
            />
            <span className="input-suffix">days</span>
          </div>
        </div>

        <div className="retention-info">
          <span className="info-item">
            Recordings before <strong>{formatDate(new Date(Date.now() - retentionDays * 24 * 60 * 60 * 1000))}</strong> will be deleted
          </span>
        </div>

        <button
          className="save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Storage Info */}
      <div className="settings-card">
        <h3>Storage Information</h3>

        {storageStats ? (
          <>
            <div className="storage-stats">
              <div className="stat-item">
                <span className="stat-label">Total Storage Used</span>
                <span className="stat-value">{storageStats.totalSizeGB} GB</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Recordings</span>
                <span className="stat-value">{storageStats.fileCount || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Oldest Recording</span>
                <span className="stat-value">{formatDate(storageStats.oldestRecording)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Newest Recording</span>
                <span className="stat-value">{formatDate(storageStats.newestRecording)}</span>
              </div>
            </div>

            {/* Storage by Date */}
            {storageStats.byDate && storageStats.byDate.length > 0 && (
              <div className="storage-by-unit">
                <h4>Storage by Date ({storageStats.byDate.length} folders)</h4>
                <div className="unit-list">
                  {storageStats.byDate.map((item) => (
                    <div key={item.date} className="unit-item">
                      <span className="unit-name">{item.date}</span>
                      <span className="unit-stats">
                        {item.count} files ({formatSize(item.sizeBytes)})
                      </span>
                      <button
                        className="delete-date-btn"
                        onClick={() => handleDeleteByDate(item.date)}
                        disabled={deletingDate === item.date}
                        title={`Delete all recordings from ${item.date}`}
                      >
                        {deletingDate === item.date ? '...' : 'Delete'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="loading-stats">Loading storage information...</div>
        )}

        <button
          className="cleanup-btn"
          onClick={handleCleanup}
          disabled={cleaning}
        >
          {cleaning ? 'Cleaning...' : 'Run Cleanup Now'}
        </button>
      </div>

      {/* Auto Record */}
      <div className="settings-card">
        <h3>Auto Record</h3>
        <p className="settings-desc">
          Automatically start recording when a unit connects. Recording stops when the unit disconnects or when stopped from the server.
        </p>
        <label className="switch">
          <input
            type="checkbox"
            checked={autoRecordAll}
            onChange={(e) => handleToggleAuto(e.target.checked)}
          />
          <span className="slider" />
          <span className="switch-label">{autoRecordAll ? 'Enabled' : 'Disabled'}</span>
        </label>
      </div>

      {/* Message */}
      {message && (
        <div className={`settings-message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <SettingsPanelStyles />
    </div>
  );
}

const SettingsPanelStyles = () => (
  <style>{`
    .settings-panel {
      display: flex;
      flex-direction: column;
      gap: 20px;
      max-width: 800px;
      margin: 0 auto;
    }

    .settings-card {
      background: #ffffff;
      border: 1px solid #e8eaed;
      border-radius: 12px;
      padding: 24px;
    }

    .settings-card h3 {
      margin: 0 0 8px 0;
      font-size: 18px;
      color: #1a1a1a;
    }

    .settings-card h4 {
      margin: 20px 0 12px 0;
      font-size: 14px;
      color: #424242;
    }

    .settings-desc {
      color: #616161;
      font-size: 14px;
      margin: 0 0 20px 0;
    }

    .setting-row {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 16px;
    }

    .setting-row label {
      font-weight: 600;
      font-size: 14px;
      color: #424242;
    }

    .input-group {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .input-group input {
      padding: 12px 16px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      font-size: 16px;
      width: 120px;
      background: #fafafa;
    }

    .input-group input:focus {
      outline: none;
      border-color: #1976d2;
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
    }

    .input-suffix {
      color: #757575;
      font-size: 14px;
    }

    .retention-info {
      background: #f5f5f5;
      border-radius: 8px;
      padding: 12px 16px;
      margin-bottom: 20px;
    }

    .info-item {
      font-size: 13px;
      color: #616161;
    }

    .info-item strong {
      color: #1976d2;
    }

    .save-btn {
      padding: 12px 24px;
      background: #1976d2;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }

    .save-btn:hover:not(:disabled) {
      background: #1565c0;
    }

    .save-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .storage-stats {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 16px;
      margin-bottom: 20px;
    }

    .stat-item {
      background: #fafafa;
      border: 1px solid #e8eaed;
      border-radius: 8px;
      padding: 16px;
    }

    .stat-label {
      display: block;
      font-size: 12px;
      text-transform: uppercase;
      color: #757575;
      margin-bottom: 6px;
      font-weight: 600;
    }

    .stat-value {
      font-size: 20px;
      font-weight: 700;
      color: #1a1a1a;
    }

    .storage-by-unit {
      margin-bottom: 20px;
    }

    .unit-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .unit-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      background: #fafafa;
      border: 1px solid #e8eaed;
      border-radius: 8px;
    }

    .unit-name {
      font-weight: 600;
      flex: 1;
      font-family: monospace;
    }

    .unit-stats {
      color: #616161;
      font-size: 14px;
    }

    .delete-date-btn {
      padding: 6px 10px;
      background: #ef5350;
      color: #fff;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      transition: background 0.2s;
    }

    .delete-date-btn:hover:not(:disabled) {
      background: #d32f2f;
    }

    .delete-date-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .loading-stats {
      text-align: center;
      padding: 40px;
      color: #757575;
    }

    .cleanup-btn {
      padding: 12px 24px;
      background: #ff5722;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }

    .cleanup-btn:hover:not(:disabled) {
      background: #e64a19;
    }

    .cleanup-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    /* Toggle switch */
    .switch {
      display: inline-flex;
      align-items: center;
      gap: 12px;
      cursor: pointer;
      user-select: none;
      margin-top: 12px;
    }
    .switch input {
      display: none;
    }
    .slider {
      position: relative;
      width: 46px;
      height: 24px;
      background: #cbd5e1;
      border-radius: 999px;
      transition: background 0.2s;
    }
    .slider::after {
      content: '';
      position: absolute;
      top: 3px;
      left: 3px;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #fff;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      transition: transform 0.2s;
    }
    .switch input:checked + .slider {
      background: #34d399;
    }
    .switch input:checked + .slider::after {
      transform: translateX(22px);
    }
    .switch-label {
      font-weight: 600;
      color: #1f2937;
    }

    .settings-message {
      padding: 16px 20px;
      border-radius: 8px;
      font-weight: 500;
    }

    .message-success {
      background: #e8f5e9;
      color: #2e7d32;
      border: 1px solid #a5d6a7;
    }

    .message-error {
      background: #ffebee;
      color: #c62828;
      border: 1px solid #ef9a9a;
    }

    .settings-panel *,
    .settings-panel *::before,
    .settings-panel *::after {
      box-sizing: border-box;
    }
    .settings-panel {
      width: 100%;
      min-width: 0;
    }
    .unit-item .unit-name {
      min-width: 0;
      overflow-wrap: anywhere;
    }

    /* Touch screens and small viewports: 44px targets */
    @media (max-width: 1024px), (pointer: coarse) {
      .save-btn,
      .cleanup-btn,
      .delete-date-btn,
      .input-group input {
        min-height: 44px;
      }
      .delete-date-btn {
        min-width: 64px;
      }
      .switch {
        min-height: 44px;
      }
    }

    @media (max-width: 768px) {
      .settings-panel {
        gap: 14px;
      }
      .settings-card {
        padding: 18px 16px;
      }
      .settings-card h3 {
        font-size: 17px;
      }
      .settings-desc {
        margin-bottom: 14px;
      }
      .storage-stats {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 10px;
      }
      .stat-item {
        padding: 12px;
      }
      .stat-label {
        font-size: 11px;
      }
      .stat-value {
        font-size: 16px;
        overflow-wrap: anywhere;
      }
      .input-group input {
        flex: 1 1 auto;
        width: auto;
        min-width: 0;
      }
      .save-btn,
      .cleanup-btn {
        width: 100%;
        min-height: 48px;
      }
      .unit-item {
        flex-wrap: wrap;
        padding: 10px 12px;
      }
      .unit-item .unit-name {
        flex: 1 1 100%;
      }
      .unit-item .unit-stats {
        flex: 1 1 auto;
      }
    }
  `}</style>
);

export default SettingsPanel;
